import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import type { AppDispatch } from './index';
import type { CustomField } from '../types/customFields';

export type { CustomField };

interface CustomFieldsState {
  fields: CustomField[];
  isLoading: boolean;
  error: string | null;
}

const initialState: CustomFieldsState = {
  fields: [],
  isLoading: false,
  error: null,
};

const customFieldsSlice = createSlice({
  name: 'customFields',
  initialState,
  reducers: {
    setCustomFields(state, action: PayloadAction<CustomField[]>) {
      state.fields = action.payload;
    },
    addCustomField(state, action: PayloadAction<CustomField>) {
      state.fields.push(action.payload);
    },
    removeCustomField(state, action: PayloadAction<string>) {
      state.fields = state.fields.filter((f) => f.fieldName !== action.payload);
    },
    updateCustomField(state, action: PayloadAction<CustomField>) {
      const idx = state.fields.findIndex((f) => f.fieldName === action.payload.fieldName);
      if (idx !== -1) {
        state.fields[idx] = action.payload;
      } else {
        // rename case: old key no longer exists; the caller should removeCustomField first
        state.fields.push(action.payload);
      }
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const { setCustomFields, addCustomField, removeCustomField, updateCustomField, setLoading, setError } =
  customFieldsSlice.actions;

export const fetchCustomFields =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await apiService.listCustomFields();
      dispatch(setCustomFields(response.data ?? []));
    } catch (error: any) {
      dispatch(setError(error?.message ?? 'Failed to load custom fields'));
    } finally {
      dispatch(setLoading(false));
    }
  };

export default customFieldsSlice.reducer;
